import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { Play, Plus } from "lucide-react"
import { ScenarioCard } from "@/components/domain/scenario-card"
import { Dialog, DialogBody, DialogFooter, DialogHeader } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import type { TestLabScenario, TestRunStatus } from "@/types/test_lab"

type Filter = "all" | "passing" | "failing" | "errored" | "never_run" | "skipped"

type Props = {
  scenarios: TestLabScenario[]
  selectedId: number | null
  onSelect: (id: number) => void
  onRunOne: (scenarioId: number) => void
  onRunAll: () => void
  busyScenarioIds: Set<number>     // scenarios whose run start is in flight (UI-only optimistic state)
  runAllBusy: boolean
  // Skill id is needed to wire the "+ Add scenario" POST. Optional so
  // any consumer that hasn't passed it yet falls back gracefully (the
  // button just disables itself).
  skillId?: number
}

export function ScenarioList({ scenarios, selectedId, onSelect, onRunOne, onRunAll, busyScenarioIds, runAllBusy, skillId }: Props) {
  const [filter, setFilter] = useState<Filter>("all")
  const [search, setSearch] = useState("")
  const [addOpen, setAddOpen] = useState(false)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return scenarios.filter((s) => {
      if (!matchesFilter(s.latestRunStatus, s.latestRunId, filter)) return false
      if (!q) return true
      return (
        (s.name ?? "").toLowerCase().includes(q) ||
        s.key.toLowerCase().includes(q) ||
        (s.goal ?? "").toLowerCase().includes(q)
      )
    })
  }, [scenarios, filter, search])

  const summary = useMemo(() => buildSummary(scenarios), [scenarios])

  return (
    <div className="flex h-full flex-col rounded-card border border-border bg-app-panel">
      <div className="border-b border-border p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-medium text-app-text">Scenarios</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              disabled={skillId == null}
              className="inline-flex items-center gap-1.5 rounded-control border border-border bg-app-bg px-3 py-1.5 text-xs font-medium text-app-text transition hover:bg-white/5 disabled:opacity-50"
              title={skillId == null ? "Skill id missing" : "Manually add a scenario"}
            >
              <Plus size={12} />
              Add scenario
            </button>
            <button
              type="button"
              onClick={onRunAll}
              disabled={runAllBusy || scenarios.length === 0}
              className="inline-flex items-center gap-1.5 rounded-control border border-brand-red bg-brand-red px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-redDark disabled:opacity-50"
            >
              <Play size={12} fill="currentColor" />
              {runAllBusy ? "Starting…" : "Run all"}
            </button>
          </div>
        </div>

        <SuiteSummary summary={summary} />

        <div className="mt-3 flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as Filter)}
            className="rounded-control border border-border bg-app-bg px-2 py-1.5 text-xs text-app-text focus:border-brand-red focus:outline-none"
          >
            <option value="all">All</option>
            <option value="failing">Failing</option>
            <option value="passing">Passing</option>
            <option value="errored">Errored</option>
            <option value="never_run">Never run</option>
            <option value="skipped">Skipped</option>
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search scenarios…"
            className="min-w-0 flex-1 rounded-control border border-border bg-app-bg px-3 py-1.5 text-xs text-app-text placeholder:text-app-muted focus:border-brand-red focus:outline-none"
          />
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {filtered.length === 0 ? (
          <div className="p-6 text-sm text-app-muted">
            {scenarios.length === 0
              ? "No scenarios for this skill yet."
              : "No matches for the current filter."}
          </div>
        ) : (
          filtered.map((s) => (
            <ScenarioCard
              key={s.id}
              scenario={s}
              selected={s.id === selectedId}
              onSelect={() => onSelect(s.id)}
              onRun={() => onRunOne(s.id)}
              busy={busyScenarioIds.has(s.id)}
            />
          ))
        )}
      </div>

      <div className="border-t border-border px-4 py-2 text-[11px] text-app-muted">
        {filtered.length} of {scenarios.length} scenarios
      </div>

      {skillId != null && (
        <AddScenarioDialog open={addOpen} onClose={() => setAddOpen(false)} skillId={skillId} />
      )}
    </div>
  )
}

// Manual create dialog. Forge's draft_red_team_suite is the AI-assisted
// path — this is the bare-hands version when the operator already knows
// the scenario they want.
function AddScenarioDialog({ open, onClose, skillId }: { open: boolean; onClose: () => void; skillId: number }) {
  const [name, setName]       = useState("")
  const [persona, setPersona] = useState("")
  const [goal, setGoal]       = useState("")
  const [submitting, setSub]  = useState(false)
  const [err, setErr]         = useState<string | null>(null)

  const close = () => { onClose(); setName(""); setPersona(""); setGoal(""); setErr(null) }
  const valid = name.trim() && persona.trim() && goal.trim()

  async function submit() {
    if (!valid) return
    setSub(true); setErr(null)
    try {
      const csrf = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
      const res = await fetch(`/skills/${skillId}/test_scenarios`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrf },
        body: JSON.stringify({ name, persona, goal }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setErr((data?.errors && data.errors[0]) || `Failed (${res.status})`)
        return
      }
      // Inertia partial reload — the parent already subscribes to a refresh
      // of the `scenarios` prop on test_run cable events; reuse that path.
      router.reload({ only: ["scenarios"] })
      close()
    } finally {
      setSub(false)
    }
  }

  return (
    <Dialog open={open} onClose={close} size="md">
      <DialogHeader title="Add scenario" subtitle="Manual create — Forge can also draft a whole suite." />
      <DialogBody>
        <label className="block text-sm">
          <span className="mb-1 block text-app-muted">Name</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="VIP user asks for cashout status" />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-app-muted">Persona</span>
          <Textarea
            rows={3}
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            placeholder="Long-time VIP user, polite, asks for plain-English status updates."
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-app-muted">Goal</span>
          <Textarea
            rows={3}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="Find out why their cashout is pending and what to do next."
          />
        </label>
        {err && <p className="text-xs text-status-error">{err}</p>}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={close} disabled={submitting}>Cancel</Button>
        <Button type="button" variant="red" onClick={submit} disabled={!valid || submitting}>
          {submitting ? "Adding…" : "Add scenario"}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

function matchesFilter(status: TestRunStatus | null, runId: number | null, filter: Filter): boolean {
  switch (filter) {
    case "all":       return true
    case "passing":   return status === "passed"
    case "failing":   return status === "failed"
    case "errored":   return status === "errored"
    case "never_run": return runId == null
    case "skipped":   return status === "skipped"
  }
}

function buildSummary(scenarios: TestLabScenario[]) {
  let passed = 0, failed = 0, errored = 0, skipped = 0, neverRun = 0
  for (const s of scenarios) {
    if (s.latestRunId == null) { neverRun += 1; continue }
    if (s.latestRunStatus === "passed")  passed  += 1
    else if (s.latestRunStatus === "failed")  failed  += 1
    else if (s.latestRunStatus === "errored") errored += 1
    else if (s.latestRunStatus === "skipped") skipped += 1
  }
  return { total: scenarios.length, passed, failed, errored, skipped, neverRun }
}

function SuiteSummary({ summary }: { summary: ReturnType<typeof buildSummary> }) {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-app-muted">
      <span>{summary.total} total</span>
      {summary.passed > 0  && <span className="text-status-success">✓ {summary.passed} passed</span>}
      {summary.failed > 0  && <span className="text-status-error">✗ {summary.failed} failed</span>}
      {summary.errored > 0 && <span className="text-status-warning">⚠ {summary.errored} errored</span>}
      {summary.skipped > 0 && <span>⏸ {summary.skipped} skipped</span>}
      {summary.neverRun > 0 && <span>◌ {summary.neverRun} never run</span>}
    </div>
  )
}
